/**
 * FILE: packages/supabase/src/services/studentNotes.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import { ServiceError } from '../internal/errors'
import type { Database } from '../types'
import { isDemoPhone, demoStudentNotes } from './demoData'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

function getToken(): string | null {
  try {
    return localStorage.getItem('fintree_student_token')
  } catch {
    return null
  }
}

type StudentNoteRow = Database['public']['Tables']['student_notes']['Row']

type RowWithAuthor = StudentNoteRow & {
  author: { name: string | null } | null
}

export interface StudentNote {
  id: string
  phone: string
  content: string
  created_by: string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
}

function toStudentNote(row: RowWithAuthor): StudentNote {
  return {
    id: row.id,
    phone: row.phone,
    content: row.content,
    created_by: row.created_by,
    created_by_name: row.author?.name ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  }
}

export async function getStudentNotes(phone: string, client: DbClient = defaultDbClient): Promise<StudentNote[]> {
  if (isDemoPhone(phone)) {
    return demoStudentNotes.map((n) => ({
      id: n.id,
      phone: n.phone,
      content: n.content,
      created_by: null,
      created_by_name: null,
      created_at: n.created_at,
      updated_at: n.created_at,
    }))
  }
  const { data, error } = await client
    .from('student_notes')
    .select('*, author:staff!student_notes_created_by_fkey(name)')
    .eq('phone', phone)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) throw new ServiceError(error.message, 'STUDENT_NOTES_FETCH', error)
  return (data ?? []).map((r) => toStudentNote(r as RowWithAuthor))
}

export async function createStudentNote(
  payload: { phone: string; content: string },
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<StudentNote> {
  if (isDemoPhone(payload.phone)) {
    return {
      id: 'demo-note-new',
      phone: payload.phone,
      content: payload.content.trim(),
      created_by: actor.userId,
      created_by_name: actor.userName,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
  }
  const content = payload.content.trim()
  if (!content) throw new ServiceError('Note content cannot be empty', 'STUDENT_NOTE_CREATE')

  const { data, error } = await client
    .from('student_notes')
    .insert({
      phone: payload.phone,
      content,
      created_by: actor.userId,
    })
    .select('*, author:staff!student_notes_created_by_fkey(name)')
    .single()

  if (error) throw new ServiceError(error.message, 'STUDENT_NOTE_CREATE', error)
  if (!data) throw new ServiceError('No note returned after insert', 'STUDENT_NOTE_CREATE')

  const note = toStudentNote(data as RowWithAuthor)

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_STUDENT_NOTE',
      entityType: 'student',
      entityId: payload.phone,
      newValues: { id: note.id, content: note.content },
    }), client
  )

  return note
}

export async function deleteStudentNote(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  if (getToken()?.startsWith('demo-token-')) return
  const { data: existing, error: fetchError } = await client
    .from('student_notes')
    .select('*')
    .eq('id', id)
    .maybeSingle()

  if (fetchError) throw new ServiceError(fetchError.message, 'STUDENT_NOTE_DELETE', fetchError)
  if (!existing) throw new ServiceError('Note not found', 'STUDENT_NOTE_DELETE')

  const { error: deleteError } = await client.from('student_notes').update({ deleted_at: new Date().toISOString() }).eq('id', id)
  if (deleteError) throw new ServiceError(deleteError.message, 'STUDENT_NOTE_DELETE', deleteError)

  await logActivity(
    withActivityActor(actor, {
      action: 'DELETE_STUDENT_NOTE',
      entityType: 'student',
      entityId: existing.phone,
      oldValues: { id: existing.id, content: existing.content },
    }), client
  )
}
